import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonComponent, PaginationComponent, SearchInputComponent } from '@dental-ui/ui';
import { LucideRefreshCw, LucideSearch } from '@lucide/angular';
import {
  PortalAdminListResult,
  PortalAdminResource,
  findPortalAdminResource,
} from '@dental-ui/portal-dashboard';
import { PortalResourceApiService } from '../data-access/portal-resource-api.service';

@Component({
  selector: 'lib-portal-resource-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonComponent,
    PaginationComponent,
    SearchInputComponent,
    LucideRefreshCw,
    LucideSearch,
  ],
  templateUrl: './portal-resource-list.component.html',
  styleUrl: './portal-resource-list.component.scss',
})
export class PortalResourceListComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly apiService = inject(PortalResourceApiService);
  private readonly destroyRef = inject(DestroyRef);

  readonly resource = signal<PortalAdminResource | null>(null);
  readonly result = signal<PortalAdminListResult | null>(null);
  readonly isLoading = signal(false);
  readonly errorMessage = signal('');

  searchTerm = '';
  private page = 0;
  private pageSizeValue = 10;

  readonly rows = computed(() => this.result()?.data ?? []);
  readonly currentPage = computed(() => this.result()?.page ?? this.page);
  readonly pageSize = computed(() => this.result()?.pageSize ?? this.pageSizeValue);
  readonly totalPages = computed(() => this.result()?.totalPages ?? 0);
  readonly totalItems = computed(() => this.result()?.totalItems ?? 0);
  readonly visibleColumns = computed(() => {
    const selectedResource = this.resource();
    const firstRow = this.rows()[0];

    if (selectedResource?.displayColumns.length) {
      return selectedResource.displayColumns;
    }

    return firstRow ? Object.keys(firstRow).slice(0, 6) : [];
  });

  constructor() {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.resource.set(findPortalAdminResource(params.get('resource')) ?? null);
      this.page = 0;
      this.result.set(null);
      this.load();
    });
  }

  onSearch(value: string): void {
    this.searchTerm = value;
    this.page = 0;
    this.load();
  }

  reload(): void {
    this.load();
  }

  onPageChange(page: number): void {
    this.page = page;
    this.load();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSizeValue = pageSize;
    this.page = 0;
    this.load();
  }

  rowIndex(index: number): number {
    return index;
  }

  columnLabel(column: string): string {
    const labels = this.resource()?.columnLabels;
    if (labels?.[column]) {
      return labels[column];
    }

    return column
      .replace(/([A-Z])/g, ' $1')
      .replace(/[_-]+/g, ' ')
      .trim()
      .replace(/^./, (char) => char.toUpperCase());
  }

  cellValue(row: Record<string, unknown>, column: string): unknown {
    if (Object.prototype.hasOwnProperty.call(row, column)) {
      return row[column];
    }

    const match = Object.keys(row).find((key) => key.toLowerCase() === column.toLowerCase());
    return match ? row[match] : undefined;
  }

  formatValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (typeof value === 'boolean') {
      return value ? 'Evet' : 'Hayır';
    }

    if (value instanceof Date) {
      return value.toLocaleDateString('tr-TR');
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  }

  private load(): void {
    const selectedResource = this.resource();

    if (!selectedResource) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set('');

    this.apiService
      .loadResource(selectedResource, {
        page: this.page,
        pageSize: this.pageSizeValue,
        searchTerm: this.searchTerm,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.result.set(result);
          this.isLoading.set(false);
        },
        error: () => {
          this.result.set(null);
          this.errorMessage.set('Kayıtlar yüklenirken bir hata oluştu.');
          this.isLoading.set(false);
        },
      });
  }
}
